import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { GiftCardTile } from './GiftCardTile';
import { GiftCardQuickAdd } from './GiftCardQuickAdd';
import { getRewardName } from '../lib/rewardImage';
import { getRewardStartingPaise } from '../lib/rewardCatalogue';
import { getRewardPricing, getRewardDenominations, getRewardRange } from '../lib/rewardDiscount';
import { useGiftCardCart } from '../context/GiftCardCartContext';
import { formatMoney } from '../lib/money';

/**
 * A gift card in the catalogue grid.
 *
 * The card itself is a link and nothing else — brand, value, discount, how many
 * values exist. Configuration happens in the quick-add sheet or on the product
 * page, never inline on the tile: an earlier version drew a denomination panel
 * over the artwork, which is the smallest surface on the page and the one the
 * customer is using to recognise the brand.
 *
 * The cart button sits outside that link so it can do its own thing. One value
 * on offer means there is nothing to choose, so it goes straight into the cart;
 * anything else opens the sheet. Neither route navigates — the product page is
 * for terms, redemption and expiry, and quick add exists precisely to avoid it.
 */
export function RewardCard({ reward }) {
    const { addGiftCard } = useGiftCardCart();
    const [sheetOpen, setSheetOpen] = useState(false);

    const name = getRewardName(reward);

    // Read, never derived. The server clamps a discount that would sell below
    // cost, so a face value cannot be recovered from a price — see
    // lib/rewardDiscount.
    const pricing = getRewardPricing(reward);
    const denominations = getRewardDenominations(reward);
    const range = getRewardRange(reward);
    const isRange = !!range && !denominations.length;

    // Fallback for a card the catalogue has priced but not decorated.
    const startingValue = pricing?.face ?? pricing?.price ?? getRewardStartingPaise(reward) / 100;
    const onlyOption = denominations.length === 1 ? denominations[0] : null;

    const onCartClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Nothing to choose — one value, so the sheet would only ask a question
        // with a single answer.
        if (onlyOption) {
            try {
                const { added } = addGiftCard(reward, onlyOption.price, 1);
                if (added === 0) {
                    toast.error(`Your cart already holds the maximum of this card.`);
                    return;
                }
                toast.success('Added to cart');
            } catch (error) {
                toast.error(error.message);
            }
            return;
        }

        setSheetOpen(true);
    };

    return (
        <>
            <div className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_14px_34px_-18px_rgba(15,23,42,0.35)]">
                <Link
                    to={`/product/reward_${reward.id}`}
                    state={{ reward }}
                    className="block"
                    data-testid={`reward-card-${reward.id}`}
                >
                    <div className="p-3 pb-0">
                        <GiftCardTile reward={reward || undefined} size="sm" />
                    </div>

                    <div className="p-3 pr-12">
                        <h3 className="line-clamp-1 font-body text-[13px] font-semibold text-secondary transition-colors group-hover:text-primary">
                            {name}
                        </h3>

                        {/* The card's value, not its price. "From" because this is
                            the cheapest of several. */}
                        <p className="mt-0.5 flex items-baseline gap-x-1 font-body text-[13px] font-bold text-secondary">
                            {(denominations.length > 1 || isRange) && (
                                <span className="font-normal text-[11px] text-muted-foreground">From</span>
                            )}
                            {formatMoney(startingValue)}
                        </p>

                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                            {pricing?.label && (
                                <span
                                    className="rounded-full bg-success/10 px-2 py-0.5 font-body text-[10px] font-bold uppercase tracking-wide text-success"
                                    data-testid={`reward-discount-${reward.id}`}
                                >
                                    {pricing.label}
                                </span>
                            )}
                            {denominations.length > 1 && (
                                <span className="font-body text-[11px] text-muted-foreground">
                                    {denominations.length} gift card values
                                </span>
                            )}
                            {isRange && (
                                <span className="font-body text-[11px] text-muted-foreground">
                                    {formatMoney(range.minFace)} – {formatMoney(range.maxFace)}
                                </span>
                            )}
                        </div>
                    </div>
                </Link>

                {/* Outside the link, or it would inherit the navigation. */}
                <button
                    onClick={onCartClick}
                    aria-label={onlyOption ? `Add ${name} to cart` : `Choose a value for ${name}`}
                    className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-muted-foreground shadow-sm transition-colors hover:border-primary hover:bg-primary hover:text-white"
                    data-testid={`reward-add-${reward.id}`}
                >
                    <ShoppingCart className="h-4 w-4" />
                </button>
            </div>

            <GiftCardQuickAdd
                reward={reward || undefined}
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
            />
        </>
    );
}
